import { useCallback, useState, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
  Button,
  Tooltip,
} from '@coreai/fluentui-react';
import { ArrowMaximizeRegular } from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { ExpandableEditorDialog } from './ExpandableEditorDialog';
import { useRestoreFocusOnDialogClose } from './useRestoreFocusOnDialogClose';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import { MULTILINE_TEXT_MAX_HEIGHT } from './constants';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import { generateSegmentId, ValueSegmentType, isValueSegmentArray, segmentsToString } from '@/types/valueSegment';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  // Wrapper for input + preview overlay positioning.
  inputWrapper: {
    position: 'relative',
  },
  // When the expand button is present, reserve inline-end space on the
  // editor so long first-line text and the internal scrollbar don't run
  // under the 24px glyph. Applied to the wrapper so it cascades into the
  // TokenizedInput's own editor without piercing its DOM.
  inputWrapperWithExpand: {
    '& [role="textbox"]': {
      paddingInlineEnd: tokens.spacingHorizontalXXL,
    },
  },
  // Expand button sits in the top-right corner of the input box. Placing
  // it inside the editor (not the label row) keeps the row's {x}/fx chips
  // uncluttered and matches the common "expand editor" affordance
  // pattern (GitHub comment editor, VS Code, Notion).
  expandButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    insetInlineEnd: tokens.spacingHorizontalXS,
    // Keep above the editor's text but not the autocomplete dropdown.
    zIndex: 1,
    minWidth: 'auto',
  },
});

export interface TextAreaFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  rows?: number | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale) */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered inside the textarea's bordered box on the
   * right edge, top-aligned to the first row of text. Used to host
   * inline affordances like `FieldActionChips`.
   */
  endActions?: ReactNode | undefined;
  /**
   * Optional override for the editor's max-height cap. Accepts any valid
   * CSS length or clamp() expression. Defaults to `MULTILINE_TEXT_MAX_HEIGHT`.
   * Pass `null` to opt out of the cap entirely (rare — long content will
   * push the drawer's other fields off-screen).
   */
  maxHeight?: string | number | null | undefined;
}

/**
 * TextAreaField - Multi-line text input with token support
 * Displays tokens as visual badges that can be clicked to view/edit
 */
export const TextAreaField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
  endActions,
  maxHeight,
}: TextAreaFieldProps) => {
  const styles = useStyles();
  const { isFocused, containerHandlers, previewHandlers, previewToggleSlotRef } = useFieldPreviewFocus();

  // Convert string to segments if needed
  const normalizedValue: ValueSegment[] = isValueSegmentArray(value)
    ? value
    : typeof value === 'string' && value
      ? [{
          id: generateSegmentId(),
          type: ValueSegmentType.LITERAL,
          value: value,
        }]
      : [];

  // Get the string value for preview
  const stringValue = segmentsToString(normalizedValue);

  // Effective cap: caller opt-out via null; otherwise their explicit value
  // or the design-system default.
  const effectiveMaxHeight =
    maxHeight === null ? undefined : maxHeight ?? MULTILINE_TEXT_MAX_HEIGHT;

  // Handle change from TokenizedInput
  const handleChange = useCallback((segments: ValueSegment[]) => {
    onChange(segments);
  }, [onChange]);

  // Expand-to-dialog affordance. Only wired when the field is not disabled
  // — disabled fields are read-only and don't need a larger editor surface.
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canExpand = !disabled;
  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);
  // Restore focus to the expand button on close — it unmounts while the dialog
  // is open, so the dialog itself can't do this (see the hook).
  const expandButtonRef = useRestoreFocusOnDialogClose<HTMLButtonElement>(isDialogOpen);

  return (
    <div className={styles.container} {...containerHandlers}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <EndActionsSlot previewToggleSlotRef={previewToggleSlotRef}>{endActions}</EndActionsSlot>
      </div>

      {/* Wrapper provides relative positioning for the overlay preview */}
      <div
        className={
          canExpand
            ? `${styles.inputWrapper} ${styles.inputWrapperWithExpand}`
            : styles.inputWrapper
        }
      >
        {/* Panel editor is unmounted while the dialog is open. This
            avoids two live-synced TokenizedInputs on the same value
            (which corrupts the panel's undo stack on every dialog
            keystroke) and eliminates redundant highlight/parse work
            for a hidden editor. When the dialog closes, the panel
            editor remounts with the current parent value. */}
        {!isDialogOpen && (
          <TokenizedInput
            name={name}
            ariaLabel={label}
            value={normalizedValue}
            onChange={handleChange}
            placeholder={placeholder || description}
            multiline={true}
            maxHeight={effectiveMaxHeight}
            disabled={disabled}
            readonly={false}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={actionOutputs}
          />
        )}

        {/* Expand-to-dialog button, top-right inside the input box.
            Kept out of the labelRow so it doesn't crowd {x}/fx chips. */}
        {canExpand && !isDialogOpen && (
          <Tooltip content={`Open ${label} in editor`} relationship="label">
            <Button
              ref={expandButtonRef as unknown as React.Ref<never>}
              className={styles.expandButton}
              appearance="subtle"
              size="small"
              icon={<ArrowMaximizeRegular />}
              onClick={openDialog}
              aria-label={`Open ${label} in editor`}
            />
          </Tooltip>
        )}

        {/* Show preview as overlay when focused and has value */}
        {isFocused && stringValue && !isDialogOpen && (
          <ExpressionPreview
            value={stringValue}
            actionOutputs={actionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            mode="overlay"
            {...previewHandlers}
          />
        )}
      </div>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}

      {/* Full-height dialog. When open, this is the only mounted editor
          for this field — the panel one above is unmounted. Fluent
          Dialog handles focus restoration to the trigger element. */}
      {canExpand && isDialogOpen && (
        <ExpandableEditorDialog
          open={isDialogOpen}
          onClose={closeDialog}
          label={label}
          value={normalizedValue}
          onChange={handleChange}
          placeholder={placeholder || description}
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      )}
    </div>
  );
};

TextAreaField.displayName = 'TextAreaField';
